import { createSelector } from 'reselect';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { RootState } from 'reducers';
import {
  getProductsInShoppingCart,
  getShoppingCartDiscountsDict,
  getTotal,
  getTotalNet,
  getTotalTax,
  getVisibleTotalDiscount,
} from 'reducers/ShoppingCart';
import { publish } from 'services/CentrifugoWS';
import {
  getCurrencyFormatter,
  getShowPricesWithTax,
} from 'reducers/configs/settings';
import {
  getChannelName,
  getTipProductId,
} from 'plugins/customerDisplay/rdx/reducer';
import { ACTIONS } from 'plugins/customerDisplay/constants/actions';
import { getProductsUniversal } from 'actions/productsDB';
import { addProduct } from 'actions/ShoppingCart';

const getCustomerDisplayData = createSelector(
  state => getShowPricesWithTax(state),
  state => getProductsInShoppingCart(state),
  state => getVisibleTotalDiscount(state),
  state => getTotalTax(state),
  state => getTotalNet(state),
  state => getTotal(state),
  state => getCurrencyFormatter(state),
  state => getShoppingCartDiscountsDict(state),
  (
    isWithTax,
    shoppingCart,
    totalDiscount,
    totalVat,
    totalNet,
    total,
    formatCurrency,
    shoppingCartDiscounts,
  ) => {
    return {
      products: shoppingCart.map(o => ({
        id: o.productID,
        name: o.name,
        code: o.code,
        qty: o.amount,
        disc: (shoppingCartDiscounts[o.rowNumber] || [])
          ?.map(a => `${a.affected}x -${a.percentage}%`)
          .join('\n'),
        price: isWithTax
          ? formatCurrency(Number(o.priceListPriceWithVAT))
          : formatCurrency(Number(o.priceListPrice)),
      })),
      summary: {
        disc: formatCurrency(totalDiscount, { roundDown: true }),
        tax: formatCurrency(totalVat),
        net: formatCurrency(totalNet),
        total: formatCurrency(total),
      },
    };
  },
);
type PercentageTip = { percentage: number };
type AmountTip = { amount: number };

export const handleTipProductApproval = (
  input: PercentageTip | AmountTip,
) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const channelName = getChannelName(getState());
  const total = getTotal(getState());
  const tipProductId = getTipProductId(getState());
  const {
    products: [tipProduct],
  } = await dispatch(getProductsUniversal({ productID: tipProductId }));
  const channel = `customer_display:${channelName}`;
  const data = getCustomerDisplayData(getState());

  if ('percentage' in input) {
    const { percentage } = input;
    if (!percentage) {
      return;
    }
    const price = ((total * percentage) / 100).toFixed(2);
    const name = `${tipProduct.name} (${percentage}%)`;
    await dispatch(
      addProduct(
        {
          productID: tipProductId,
          basePrice: price,
          name,
          amount: 1,
        },
        {
          extraParams: {
            displayTipPercentage: percentage,
          },
        },
      ),
    );
    publish({
      channel,
      message: {
        action: ACTIONS.MISC.ADDED_TIP,
        onlyTipData: true,
        product: {
          id: tipProductId,
          name,
          code: tipProduct.code,
          qty: 1,
          disc: 0,
          price,
        },
        summary: data.summary,
      },
    });
    return;
  }
  const { amount } = input;
  if (!amount) {
    return;
  }

  await dispatch(
    addProduct({
      productID: tipProductId,
      basePrice: amount.toFixed(2),
      amount: 1,
    }),
  );

  publish({
    channel,
    message: {
      action: ACTIONS.MISC.ADDED_TIP,
      onlyTipData: true,
      product: {
        id: tipProductId,
        name: tipProduct.name,
        code: tipProduct.code,
        qty: 1,
        disc: 0,
        price: amount,
      },
      summary: data.summary,
    },
  });
};
